import { z } from "zod/v4-mini";

import { TandemEventDefinition } from "@/lib/drivers/tandem/TandemTypes";
import { cz } from "@/utils/zodUtils";

export type BolusDeliveryContext =
  | "device" // Manual (0) => quick, (1) => pump
  | "algorithm" // Automated (7)
  | "remote"; // ??? (8)

export type BolusDeliveryPayload = {
  bolus_id: number;
  bolus_delivery_status?: number;
  bolus_type?: number;
  bolus_source?: number;
  requested_now?: number;
  requested_later?: number;
  correction?: number;
  extended_duration_requested?: number;
  delivered_total?: number;
};

export type BolusDeliveryEvent = {
  bolusId: number;
  bolusDeliveryStatus?: number;
  bolusType?: number;
  deliveryContext?: BolusDeliveryContext;
  requestedNow?: number;
  requestedLater?: number;
  correction?: number;
  extendedDurationRequested?: number;
  deliveredTotal?: number;
};

const bolusDeliverySchema = z.object({
  bolus_id: z.number(),
  bolus_delivery_status: cz.permissive(z.number()),
  bolus_type: cz.permissive(z.number()),
  bolus_source: cz.permissive(z.number()),
  requested_now: cz.permissive(z.number()),
  requested_later: cz.permissive(z.number()),
  correction: cz.permissive(z.number()),
  extended_duration_requested: cz.permissive(z.number()),
  delivered_total: cz.permissive(z.number()),
});

export const LID_BOLUS_DELIVERY = {
  value: 0x118,
  name: "Bolus Delivery Event",
  format: "hbbb.hhhhh",
  fields: [
    "bolus_id",
    "bolus_delivery_status",
    "bolus_type",
    "bolus_source",
    "requested_now",
    "requested_later",
    "correction",
    "extended_duration_requested",
    "delivered_total",
  ],
  postprocess: rec => ({
    bolusId: rec.bolus_id,
    bolusDeliveryStatus: rec.bolus_delivery_status,
    bolusType: rec.bolus_type,
    deliveryContext: getBolusSource(rec.bolus_source),
    requestedNow: rec.requested_now,
    requestedLater: rec.requested_later,
    correction: rec.correction,
    extendedDurationRequested: rec.extended_duration_requested,
  }),
  schema: bolusDeliverySchema,
} as const satisfies TandemEventDefinition<BolusDeliveryPayload, BolusDeliveryEvent>;

const getBolusSource = (bolusSource: number | undefined): BolusDeliveryContext | undefined => {
  switch (bolusSource) {
    case 0:
    case 1:
      return "device";
    case 7:
      return "algorithm";
    case 8:
      return "remote";
    default:
      return undefined;
  }
};
